import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TaskDetailModal } from "@/components/tasks/TaskDetailModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { getCategoryConfig } from "@/lib/taskCategoryConfig";
import { format, formatDistanceToNow } from "date-fns";
import { 
  Search, 
  Calendar, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  ListTodo,
  PlayCircle,
  XCircle
} from "lucide-react";
import confetti from "canvas-confetti";
import { AssignmentStatusBadge } from "@/components/tasks/AssignmentStatusBadge";

interface AssignedTask {
  id: string;
  assignment_id: string;
  assignment_status: string;
  assigned_at: string;
  accepted_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  assigned_by_id: string;
  assigned_by_name: string;
  assigned_by_role: string;
  task_title: string;
  task_description: string | null;
  status: string;
  due_date: string | null;
  priority_quadrant: string | null;
  category_type: string;
  custom_category: string | null;
  checkpoints: any[];
  tags: string[];
  created_at: string;
  user_id: string;
  una_id: string;
  is_direct_admin_task?: boolean;
}

export default function MyAssignedTasks() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState<AssignedTask[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<AssignedTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedTask, setSelectedTask] = useState<AssignedTask | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Modal states
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  
  // Form states
  const [startImmediately, setStartImmediately] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [startNotes, setStartNotes] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [allWorkComplete, setAllWorkComplete] = useState(false);
  const [blockingReason, setBlockingReason] = useState("");
  const [markAsBlocked, setMarkAsBlocked] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    overdue: 0,
  });

  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAssignedTasks();
    }
  }, [user]);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchQuery, statusFilter, categoryFilter]);

  useEffect(() => {
    const taskId = searchParams.get("task");
    if (taskId && tasks.length > 0) {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        setSelectedTask(task);
        setDetailModalOpen(true);
      }
    }
  }, [searchParams, tasks]);

  const loadAssignedTasks = async () => {
    if (!user) return;

    setLoading(true);

    try {
      let transformedTasks: AssignedTask[] = [];
      
      // First, try to get the current user's una_member record (may not exist for Treasury Secretaries)
      const { data: memberData } = await supabase
        .from("una_members")
        .select("id, una_id, full_name, member_role")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      if (memberData) {
        setCurrentMemberId(memberData.id);

        // Query task_assignments with joins to get full task details
        const { data: assignmentsData, error: assignmentsError } = await supabase
          .from("task_assignments")
          .select(`
            id,
            status,
            assigned_at,
            accepted_at,
            started_at,
            completed_at,
            assigned_by,
            task:custom_tasks (
              id,
              task_title,
              task_description,
              status,
              due_date,
              priority_quadrant,
              category_type,
              custom_category,
              checkpoints,
              tags,
              created_at,
              user_id,
              una_id
            )
          `)
          .eq("una_member_id", memberData.id)
          .order("assigned_at", { ascending: false });

        if (!assignmentsError && assignmentsData) {
          // Get unique assigner user_ids
          const assignerUserIds = [...new Set(assignmentsData.map(a => a.assigned_by).filter(Boolean))];
          
          // Fetch assigner details using user_id
          const { data: assigners } = await supabase
            .from("una_members")
            .select("user_id, full_name, member_role")
            .in("user_id", assignerUserIds);

          // Create a lookup map
          const assignerMap = new Map(
            assigners?.map(a => [a.user_id, a]) || []
          );

          // Transform the data structure with assigner information
          transformedTasks = assignmentsData
            .filter(assignment => assignment.task)
            .map((assignment: any) => {
              const assigner = assignerMap.get(assignment.assigned_by);
              return {
                id: assignment.task.id,
                assignment_id: assignment.id,
                assignment_status: assignment.status,
                assigned_at: assignment.assigned_at,
                accepted_at: assignment.accepted_at,
                started_at: assignment.started_at,
                completed_at: assignment.completed_at,
                assigned_by_id: assignment.assigned_by,
                assigned_by_name: assigner?.full_name || "Unknown",
                assigned_by_role: assigner?.member_role || "",
                task_title: assignment.task.task_title,
                task_description: assignment.task.task_description,
                status: assignment.task.status,
                due_date: assignment.task.due_date,
                priority_quadrant: assignment.task.priority_quadrant,
                category_type: assignment.task.category_type,
                custom_category: assignment.task.custom_category,
                checkpoints: assignment.task.checkpoints || [],
                tags: assignment.task.tags || [],
                created_at: assignment.task.created_at,
                user_id: assignment.task.user_id,
                una_id: assignment.task.una_id,
                is_direct_admin_task: false,
              };
            });
        }
      }

      // Also load admin-assigned tasks directly from custom_tasks (works for all users including Treasury Secretaries)
      const { data: adminTasks, error: adminTasksError } = await supabase
        .from("custom_tasks")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_admin_assigned", true)
        .order("created_at", { ascending: false });

      if (!adminTasksError && adminTasks) {
        const adminTransformedTasks: AssignedTask[] = adminTasks
          .filter(task => !transformedTasks.some(t => t.id === task.id)) // Avoid duplicates
          .map(task => ({
            id: task.id,
            assignment_id: task.id, // Use task ID as assignment ID for admin tasks
            assignment_status: task.status === "completed" ? "completed" : 
                              task.status === "in_progress" ? "in_progress" : "pending",
            assigned_at: task.created_at || new Date().toISOString(),
            accepted_at: null,
            started_at: task.status === "in_progress" ? task.created_at : null,
            completed_at: task.completed_at,
            assigned_by_id: task.created_by_admin_id || "",
            assigned_by_name: "Wealthy Steward Advisor",
            assigned_by_role: "advisor",
            task_title: task.task_title,
            task_description: task.task_description,
            status: task.status || "not_started",
            due_date: task.due_date,
            priority_quadrant: null,
            category_type: task.category_type || "other",
            custom_category: task.custom_category,
            checkpoints: (task.checkpoints as any[]) || [],
            tags: (task.tags as string[]) || [],
            created_at: task.created_at || new Date().toISOString(),
            user_id: task.user_id,
            una_id: task.una_id || "",
            is_direct_admin_task: true,
          }));

        transformedTasks = [...transformedTasks, ...adminTransformedTasks];
      }

      if (transformedTasks.length === 0) {
        // No tasks found from any source
        toast({
          title: "No Assigned Tasks",
          description: "You don't have any tasks assigned to you yet.",
        });
      }

      setTasks(transformedTasks);
      calculateStats(transformedTasks);
    } catch (error) {
      console.error("Error in loadAssignedTasks:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load assigned tasks",
      });
    }

    setLoading(false);
  };

  const calculateStats = (taskList: AssignedTask[]) => {
    const total = taskList.length;
    const pending = taskList.filter(t => t.assignment_status === "pending").length;
    const inProgress = taskList.filter(t => t.assignment_status === "in_progress").length;
    const overdue = taskList.filter(t => 
      t.due_date && 
      new Date(t.due_date) < new Date() && 
      t.assignment_status !== "completed"
    ).length;

    setStats({ total, pending, inProgress, overdue });
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (task) =>
          task.task_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.task_description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter - filter by ASSIGNMENT status
    if (statusFilter !== "all") {
      if (statusFilter === "overdue") {
        filtered = filtered.filter(task => 
          task.due_date && 
          new Date(task.due_date) < new Date() && 
          task.assignment_status !== "completed"
        );
      } else {
        filtered = filtered.filter((task) => task.assignment_status === statusFilter);
      }
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((task) => task.category_type === categoryFilter);
    }

    setFilteredTasks(filtered);
  };

  const transitionAssignment = async (
    newStatus: "accepted" | "in_progress" | "completed" | "blocked",
    notes?: string
  ) => {
    if (!selectedTask) return { ok: false };
    const { error } = await supabase.rpc("transition_task_assignment", {
      _assignment_id: selectedTask.assignment_id,
      _new_status: newStatus,
      _notes: notes ?? null,
    });
    if (error) {
      toast({
        variant: "destructive",
        title: "Unable to update task",
        description: error.message || "Please try again.",
      });
      return { ok: false };
    }
    return { ok: true };
  };

  const handleAcceptTask = async () => {
    if (!selectedTask || !currentMemberId) return;
    const newStatus = startImmediately ? "in_progress" : "accepted";
    const res = await transitionAssignment(newStatus);
    if (!res.ok) return;

    toast({
      title: "Success!",
      description: startImmediately
        ? "Task accepted and started!"
        : "Task accepted!",
    });

    setAcceptModalOpen(false);
    setStartImmediately(false);
    await loadAssignedTasks();
  };

  const handleDeclineTask = async () => {
    if (!selectedTask || !declineReason.trim()) return;

    // Delete the assignment
    const { error: deleteError } = await supabase
      .from("task_assignments")
      .delete()
      .eq("id", selectedTask.assignment_id);

    if (deleteError) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to decline task",
      });
      return;
    }

    // Create a comment explaining the decline
    await supabase.from("task_comments").insert({
      task_id: selectedTask.id,
      user_id: user?.id,
      comment: `Declined this task. Reason: ${declineReason}`,
    });

    // Create audit log
    await supabase.from("una_audit_log").insert({
      una_id: selectedTask.una_id,
      user_id: user?.id,
      action: "task_declined",
      resource_type: "task_assignment",
      resource_id: selectedTask.assignment_id,
      details: {
        task_id: selectedTask.id,
        task_title: selectedTask.task_title,
        reason: declineReason,
      },
    });

    toast({
      title: "Task Declined",
      description: "The assignor has been notified.",
    });

    setDeclineModalOpen(false);
    setDeclineReason("");
    await loadAssignedTasks();
  };

  const handleStartWorking = async () => {
    if (!selectedTask) return;
    const notes = startNotes.trim()
      ? `Started working. Initial notes: ${startNotes}`
      : undefined;
    const res = await transitionAssignment("in_progress", notes);
    if (!res.ok) return;

    toast({
      title: "Task Started!",
      description: "Good luck with your work!",
    });

    setStartModalOpen(false);
    setStartNotes("");
    await loadAssignedTasks();
  };

  const handleCompleteTask = async () => {
    if (!selectedTask || !allWorkComplete) return;
    const notes = completionNotes.trim()
      ? `Completed task. Notes: ${completionNotes}`
      : undefined;
    const res = await transitionAssignment("completed", notes);
    if (!res.ok) return;

    // Trigger confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    toast({
      title: "Task Completed! 🎉",
      description: "Great work!",
    });

    setCompleteModalOpen(false);
    setCompletionNotes("");
    setAllWorkComplete(false);
    await loadAssignedTasks();
  };

  const handleBlockTask = async () => {
    if (!selectedTask || !blockingReason.trim()) return;

    // Create comment with blocking reason
    await supabase.from("task_comments").insert({
      task_id: selectedTask.id,
      user_id: user?.id,
      comment: `🚧 BLOCKED: ${blockingReason}`,
    });

    // Optionally mark task as blocked
    if (markAsBlocked) {
      await supabase
        .from("custom_tasks")
        .update({ status: "blocked" })
        .eq("id", selectedTask.id);
    }

    // Create audit log
    await supabase.from("una_audit_log").insert({
      una_id: selectedTask.una_id,
      user_id: user?.id,
      action: "task_blocked",
      resource_type: "task",
      resource_id: selectedTask.id,
      details: {
        assignment_id: selectedTask.assignment_id,
        task_title: selectedTask.task_title,
        reason: blockingReason,
      },
    });

    toast({
      title: "Blocking Issue Reported",
      description: "Help is on the way!",
    });

    setBlockModalOpen(false);
    setBlockingReason("");
    setMarkAsBlocked(false);
    await loadAssignedTasks();
  };

  const calculateProgress = (checkpoints: any[]) => {
    if (!checkpoints || checkpoints.length === 0) return 0;
    const completed = checkpoints.filter((cp) => cp.completed).length;
    return Math.round((completed / checkpoints.length) * 100);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">My Assigned Tasks</h1>
          <p className="text-muted-foreground mt-1">
            Tasks assigned to you across all your UNAs
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setStatusFilter("all")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Assigned</p>
                  <p className="text-3xl font-bold mt-1">{stats.total}</p>
                </div>
                <ListTodo className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setStatusFilter("pending")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Pending Acceptance</p>
                  <p className="text-3xl font-bold mt-1 text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setStatusFilter("in_progress")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                  <p className="text-3xl font-bold mt-1 text-green-600">{stats.inProgress}</p>
                </div>
                <PlayCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setStatusFilter("overdue")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                  <p className="text-3xl font-bold mt-1 text-red-600">{stats.overdue}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="asset">Asset</SelectItem>
              <SelectItem value="contribution">Contribution</SelectItem>
              <SelectItem value="resolution">Resolution</SelectItem>
              <SelectItem value="documentation">Documentation</SelectItem>
              <SelectItem value="banking">Banking</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="governance">Governance</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <CheckCircle2 className="h-16 w-16 mx-auto text-muted-foreground opacity-50 mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {tasks.length === 0 ? "No tasks assigned yet" : "No tasks match your filters"}
                </h3>
                <p className="text-muted-foreground">
                  {tasks.length === 0
                    ? "When the primary officer assigns tasks to you, they'll appear here"
                    : "Try adjusting your search criteria or filters"}
                </p>
                {statusFilter !== "all" && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setStatusFilter("all");
                      setSearchQuery("");
                      setCategoryFilter("all");
                    }}
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task) => {
              const categoryConfig = getCategoryConfig(task.category_type as any);
              const progress = calculateProgress(task.checkpoints);
              const isOverdue =
                task.due_date &&
                new Date(task.due_date) < new Date() &&
                task.assignment_status !== "completed";

              return (
                <Card key={task.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div
                            className="p-2 rounded-lg mt-1"
                            style={{ backgroundColor: categoryConfig.color + "20" }}
                          >
                            <categoryConfig.icon
                              className="h-5 w-5"
                              style={{ color: categoryConfig.color }}
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2">
                              {task.task_title}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              <Badge
                                variant="outline"
                                style={{
                                  borderColor: categoryConfig.color,
                                  color: categoryConfig.color,
                                }}
                              >
                                {categoryConfig.name}
                              </Badge>
                              {task.priority_quadrant && (
                                <Badge variant="outline">
                                  {task.priority_quadrant.replace("_", " ")}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Assignment Context */}
                        <div className="flex items-center gap-3 mb-3 p-3 bg-muted/50 rounded-lg">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">
                              {getInitials(task.assigned_by_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              Assigned by {task.assigned_by_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(task.assigned_at), { addSuffix: true })}
                            </p>
                          </div>
                          <AssignmentStatusBadge status={task.assignment_status as any} />
                        </div>

                        {/* Description */}
                        {task.task_description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {task.task_description}
                          </p>
                        )}

                        {/* Due Date and Progress */}
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          {task.due_date && (
                            <div
                              className={`flex items-center gap-1 ${
                                isOverdue ? "text-red-600 font-semibold" : "text-muted-foreground"
                              }`}
                            >
                              <Calendar className="h-4 w-4" />
                              Due: {format(new Date(task.due_date), "MMM d, yyyy")}
                              {isOverdue && " (Overdue)"}
                            </div>
                          )}

                          {task.checkpoints && task.checkpoints.length > 0 && (
                            <div className="flex items-center gap-2 flex-1 max-w-xs">
                              <Progress value={progress} className="h-2" />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {task.checkpoints.filter((cp) => cp.completed).length}/
                                {task.checkpoints.length}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Assignment Status Badge */}
                      <div className="flex flex-col items-end gap-3">
                        {/* Action Buttons Based on Status */}
                        {task.assignment_status === "pending" && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => {
                                setSelectedTask(task);
                                setAcceptModalOpen(true);
                              }}
                              className="w-full"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Accept Task
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedTask(task);
                                setDeclineModalOpen(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Decline
                            </Button>
                          </div>
                        )}

                        {task.assignment_status === "accepted" && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => {
                                setSelectedTask(task);
                                setStartModalOpen(true);
                              }}
                              variant="default"
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              <PlayCircle className="h-4 w-4 mr-2" />
                              Start Working
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedTask(task);
                                setDeclineModalOpen(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Decline
                            </Button>
                          </div>
                        )}

                        {task.assignment_status === "in_progress" && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => {
                                setSelectedTask(task);
                                setCompleteModalOpen(true);
                              }}
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Mark Complete
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedTask(task);
                                setBlockModalOpen(true);
                              }}
                              className="border-red-500 text-red-600 hover:bg-red-50"
                            >
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Block Task
                            </Button>
                          </div>
                        )}

                        {task.assignment_status === "completed" && (
                          <div className="text-center">
                            <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-green-600">Completed</p>
                            <p className="text-xs text-muted-foreground">
                              {task.completed_at && formatDistanceToNow(new Date(task.completed_at), { addSuffix: true })}
                            </p>
                          </div>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTask(task);
                            setDetailModalOpen(true);
                          }}
                          className="w-full"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Accept Task Modal */}
        <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Accept This Task?</DialogTitle>
              <DialogDescription>
                By accepting, you acknowledge responsibility for completing this task.
              </DialogDescription>
            </DialogHeader>
            {selectedTask && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-medium">{selectedTask.task_title}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Assigned by: {selectedTask.assigned_by_name}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="start-immediately"
                    checked={startImmediately}
                    onCheckedChange={(checked) => setStartImmediately(checked as boolean)}
                  />
                  <Label htmlFor="start-immediately" className="text-sm cursor-pointer">
                    Start working immediately
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAcceptModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAcceptTask}>
                Accept
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Decline Task Modal */}
        <Dialog open={declineModalOpen} onOpenChange={setDeclineModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Decline This Task?</DialogTitle>
              <DialogDescription>
                Let the assignor know why you can't complete this task.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="decline-reason">Reason for declining (required)</Label>
                <Textarea
                  id="decline-reason"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Explain why you cannot complete this task..."
                  className="mt-2"
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeclineModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeclineTask}
                disabled={declineReason.trim().length < 10}
              >
                Decline Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Start Working Modal */}
        <Dialog open={startModalOpen} onOpenChange={setStartModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Working on This Task?</DialogTitle>
              <DialogDescription>
                This will mark the task as in progress.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="start-notes">Add initial notes (optional)</Label>
                <Textarea
                  id="start-notes"
                  value={startNotes}
                  onChange={(e) => setStartNotes(e.target.value)}
                  placeholder="Any initial thoughts or approach..."
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStartModalOpen(false)}>
                Not Yet
              </Button>
              <Button onClick={handleStartWorking} className="bg-green-600 hover:bg-green-700">
                Start Working
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Task Modal */}
        <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete This Task?</DialogTitle>
              <DialogDescription>
                Mark this task as completed and notify the assignor.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="completion-notes">Completion notes (optional)</Label>
                <Textarea
                  id="completion-notes"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Say what got done, what came of it, or what happens next..."
                  className="mt-2"
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {completionNotes.length}/500 characters
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all-complete"
                  checked={allWorkComplete}
                  onCheckedChange={(checked) => setAllWorkComplete(checked as boolean)}
                />
                <Label htmlFor="all-complete" className="text-sm cursor-pointer">
                  All work is complete and verified
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>
                Not Yet
              </Button>
              <Button
                onClick={handleCompleteTask}
                disabled={!allWorkComplete}
                className="bg-green-600 hover:bg-green-700"
              >
                Mark Complete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Task Modal */}
        <Dialog open={blockModalOpen} onOpenChange={setBlockModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Report Blocking Issue</DialogTitle>
              <DialogDescription>
                Let others know what's preventing you from completing this task.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="blocking-reason">What's blocking your progress? (required)</Label>
                <Textarea
                  id="blocking-reason"
                  value={blockingReason}
                  onChange={(e) => setBlockingReason(e.target.value)}
                  placeholder="Describe what's preventing you from completing this task..."
                  className="mt-2"
                  rows={4}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mark-blocked"
                  checked={markAsBlocked}
                  onCheckedChange={(checked) => setMarkAsBlocked(checked as boolean)}
                />
                <Label htmlFor="mark-blocked" className="text-sm cursor-pointer">
                  Mark task as blocked
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleBlockTask}
                disabled={blockingReason.trim().length < 20}
              >
                Report Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Task Detail Modal */}
        {selectedTask && (
          <TaskDetailModal
            task={{
              ...selectedTask,
              assignment_id: selectedTask.assignment_id,
              assignment_status: selectedTask.assignment_status, // Pass assignment_status for proper button state
            } as any}
            isOpen={detailModalOpen}
            onClose={() => {
              setDetailModalOpen(false);
              setSelectedTask(null);
              navigate("/my-assigned-tasks", { replace: true });
            }}
            onUpdateStatus={async (taskId: string, newStatus: string) => {
              // Reload assignments to get fresh data
              await loadAssignedTasks();
              
              // Update selectedTask to reflect the new status immediately
              if (selectedTask?.id === taskId) {
                setSelectedTask(prev => prev ? {
                  ...prev,
                  assignment_status: newStatus,
                  started_at: newStatus === 'in_progress' ? new Date().toISOString() : prev.started_at,
                  completed_at: newStatus === 'completed' ? new Date().toISOString() : prev.completed_at
                } : null);
              }
            }}
          />
        )}
      </div>
    </>
  );
}
